import React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cn } from '../../lib/utils';

/**
 * Thin shadcn-style wrappers over @radix-ui/react-dropdown-menu.
 *
 * Replacing the hand-rolled UserMenu dropdown, which managed open state
 * plus document mousedown/Escape listeners by hand. Radix gives us that
 * dismissal behaviour for free and adds what the hand-rolled version
 * lacked: arrow-key navigation between items, typeahead, roving focus,
 * focus return to the trigger on close, and correct
 * `role="menu"`/`menuitem` semantics.
 *
 * Visuals unchanged — the same concrete Tailwind classes the old menu
 * used are the defaults here, overridable via `className` (`cn()` merge).
 *
 * Note: `DropdownMenuLabel` is for non-interactive content (it is not
 * keyboard-focusable); do not put it inside `DropdownMenuItem`.
 */

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 8, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 py-1',
        className
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = 'DropdownMenuContent';

const DropdownMenuItem = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 cursor-pointer outline-none data-[highlighted]:bg-gray-50',
      className
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = 'DropdownMenuItem';

const DropdownMenuLabel = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-4 py-3', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('border-t border-gray-100 my-1', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuPortal,
};
